import React, { useState } from 'react';
import { Ticket, Plus, Copy, Check, Loader2 } from 'lucide-react';
import { api } from '../../services/apiClient';

interface Props {
  applicationId: number;
  email?: string;
}

const TEMPLATES = [
  { label: 'Podziękowanie -10%', type: 'percent' as const, value: 10, prefix: 'THX' },
  { label: 'Polecenie -15%', type: 'percent' as const, value: 15, prefix: 'REF' },
  { label: 'Rabat 50 PLN', type: 'fixed' as const, value: 50, prefix: 'RABAT' },
  { label: 'Darmowa wycieczka', type: 'percent' as const, value: 100, prefix: 'FREE' },
];

export const TripVoucherWidget: React.FC<Props> = ({ applicationId, email }) => {
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ code: string; description: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [form, setForm] = useState({ type: 'percent' as 'percent' | 'fixed', value: 10, validDays: 90, maxUses: 1, routeSlug: '' });

  const createVoucher = async (type: 'percent' | 'fixed', value: number, prefix: string) => {
    setCreating(true);
    try {
      const code = `${prefix}-${Date.now().toString(36).toUpperCase()}`;
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + (customMode ? form.validDays : 90));

      await api.post('/wycieczki/admin/vouchers', {
        code,
        discount_type: type,
        discount_value: value,
        max_uses: customMode ? form.maxUses : 1,
        valid_until: validUntil.toISOString().split('T')[0],
        route_slug: customMode && form.routeSlug ? form.routeSlug : null,
        application_id: applicationId,
      });
      setCreated({
        code,
        description: type === 'percent' ? `${value}% zniżki` : `${value} PLN rabatu`,
      });
    } catch {
      // silently handle
    } finally {
      setCreating(false);
    }
  };

  const copyCode = () => {
    if (created) {
      navigator.clipboard.writeText(created.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 flex items-center gap-2">
        <Ticket size={18} className="text-white" />
        <h3 className="text-white font-bold text-sm">Voucher / Kod rabatowy</h3>
      </div>
      <div className="p-4 space-y-3">
        {created ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <p className="text-xs text-emerald-600 mb-1">Utwórzono voucher ({created.description})</p>
            <div className="flex items-center justify-center gap-2">
              <code className="text-lg font-bold text-emerald-800 bg-white px-3 py-1 rounded border">{created.code}</code>
              <button onClick={copyCode} className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600" title="Kopiuj kod">
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            {email && <p className="text-xs text-gray-500 mt-2">Wyślij klientówi: {email}</p>}
            <button onClick={() => setCreated(null)} className="text-xs text-emerald-600 hover:underline mt-2">Utwórz kolejny</button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              {TEMPLATES.map(t => (
                <button
                  key={t.label}
                  onClick={() => createVoucher(t.type, t.value, t.prefix)}
                  disabled={creating}
                  className="text-left px-3 py-2 bg-gray-50 hover:bg-emerald-50 rounded-lg border border-gray-200 hover:border-emerald-300 transition-colors text-sm disabled:opacity-50"
                >
                  <span className="font-medium text-gray-800">{t.label}</span>
                  <span className="block text-xs text-gray-500">
                    {t.type === 'percent' ? `${t.value}%` : `${t.value} PLN`}
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={() => setCustomMode(!customMode)}
              className="text-xs text-teal-600 hover:underline flex items-center gap-1"
            >
              <Plus size={12} />
              {customMode ? 'Ukryj formularz' : 'Własny voucher'}
            </button>

            {customMode && (
              <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Typ</label>
                    <select
                      value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value as 'percent' | 'fixed' }))}
                      className="w-full border rounded px-2 py-1 text-sm"
                    >
                      <option value="percent">Procent (%)</option>
                      <option value="fixed">Kwota (PLN)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Wartość</label>
                    <input
                      type="number"
                      value={form.value}
                      onChange={e => setForm(f => ({ ...f, value: Number(e.target.value) }))}
                      className="w-full border rounded px-2 py-1 text-sm"
                      min={1}
                      max={form.type === 'percent' ? 100 : 9999}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Ważność (dni)</label>
                    <input
                      type="number"
                      value={form.validDays}
                      onChange={e => setForm(f => ({ ...f, validDays: Number(e.target.value) }))}
                      className="w-full border rounded px-2 py-1 text-sm"
                      min={1}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Max użyć</label>
                    <input
                      type="number"
                      value={form.maxUses}
                      onChange={e => setForm(f => ({ ...f, maxUses: Number(e.target.value) }))}
                      className="w-full border rounded px-2 py-1 text-sm"
                      min={1}
                    />
                  </div>
                </div>
                <button
                  onClick={() => createVoucher(form.type, form.value, 'CUSTOM')}
                  disabled={creating}
                  className="w-full bg-teal-600 text-white py-1.5 rounded text-sm font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <Ticket size={14} />}
                  {creating ? 'Tworzenie...' : 'Utwórz voucher'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
